import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import {
  CheckCircle,
  FileCheck,
  Building2,
  MapPin,
  Mail,
  Phone,
  AlertTriangle,
  Clock,
  Trash2,
  PenLine,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DocketSign = () => {
  const { token } = useParams();
  const [docket, setDocket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [completed, setCompleted] = useState(null); // 'signed' | 'expired'

  const [signedName, setSignedName] = useState('');
  const [signedEmail, setSignedEmail] = useState('');
  const [hasSignature, setHasSignature] = useState(false);

  const canvasRef = useRef(null);
  const drawing = useRef(false);

  useEffect(() => {
    document.documentElement.classList.add('dark');
    localStorage.setItem('theme', 'dark');
  }, []);

  useEffect(() => {
    const fetchDocket = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/public/dockets/sign/${token}`);
        setDocket(res.data);
        if (res.data.status === 'signed' || res.data.status === 'expired') {
          setCompleted(res.data.status);
        }
        if (res.data.requested_to) setSignedEmail(res.data.requested_to);
      } catch (err) {
        setError(err.response?.data?.detail || 'This signing link is invalid or has expired');
      } finally {
        setLoading(false);
      }
    };
    fetchDocket();
  }, [token]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: (clientX - rect.left) * (canvas.width / rect.width), y: (clientY - rect.top) * (canvas.height / rect.height) };
  };
  const startDrawing = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const draw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#111111';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
    setHasSignature(true);
  };
  const stopDrawing = () => { drawing.current = false; };
  const clearSignature = () => {
    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setHasSignature(false);
  };

  const handleSubmit = async () => {
    if (!signedName.trim()) { toast.error('Please enter your name'); return; }
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(signedEmail.trim())) { toast.error('Please enter a valid email address'); return; }
    if (!hasSignature) { toast.error('Please draw your signature'); return; }

    setProcessing(true);
    try {
      const signature_image = canvasRef.current.toDataURL('image/png');
      const res = await axios.post(`${API_URL}/api/public/dockets/sign/${token}`, {
        signed_name: signedName.trim(),
        signed_email: signedEmail.trim(),
        signature_image,
      });
      setCompleted('signed');
      toast.success(res.data.message || 'Signature recorded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit signature');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Loading docket…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-red-500" />
            <h2 className="text-xl font-bold mb-2">Link Unavailable</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            {completed === 'signed' ? (
              <>
                <CheckCircle className="h-16 w-16 mx-auto mb-4 text-green-500" />
                <h2 className="text-2xl font-bold mb-2">Signature Recorded</h2>
                <p className="text-muted-foreground mb-4">
                  Thank you for signing docket {docket?.docket_number ? `#${docket.docket_number}` : ''}.
                  A copy has been emailed to you for your records.
                </p>
              </>
            ) : (
              <>
                <Clock className="h-16 w-16 mx-auto mb-4 text-orange-500" />
                <h2 className="text-2xl font-bold mb-2">Link Expired</h2>
                <p className="text-muted-foreground mb-4">
                  This signing link has expired. Please contact {docket?.company?.name || 'the company'} for a new link.
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4" data-testid="docket-sign-page">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-[#ffd108] mb-2">{docket?.company?.name || 'EQUIPR CORE'}</h1>
          <p className="text-muted-foreground">Docket Signature</p>
        </div>

        {/* Company + docket meta */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold flex items-center gap-2"><Building2 className="h-5 w-5" /> {docket?.company?.name}</h2>
                {docket?.company?.phone && (
                  <p className="text-muted-foreground text-sm flex items-center gap-1 mt-1"><Phone className="h-3 w-3" /> {docket.company.phone}</p>
                )}
                {docket?.company?.email && (
                  <p className="text-muted-foreground text-sm flex items-center gap-1"><Mail className="h-3 w-3" /> {docket.company.email}</p>
                )}
              </div>
              <div className="text-right">
                <Badge className="bg-[#ffd108] text-black text-lg px-4 py-1 font-mono">#{docket?.docket_number}</Badge>
                {docket?.job_date && (
                  <p className="text-muted-foreground text-sm mt-2">
                    Date: {(() => { try { return format(parseISO(docket.job_date), 'dd MMM yyyy'); } catch { return docket.job_date; } })()}
                  </p>
                )}
                {docket?.po_number && <p className="text-muted-foreground text-sm">PO: {docket.po_number}</p>}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Customer / Site */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-muted-foreground text-xs mb-1">Customer</p>
                <p className="font-medium">{docket?.customer?.company_name}</p>
                {docket?.customer?.contact_name && <p className="text-muted-foreground text-sm">{docket.customer.contact_name}</p>}
              </div>
              {docket?.site && (docket.site.name || docket.site.address) && (
                <div>
                  <p className="text-muted-foreground text-xs mb-1 flex items-center gap-1"><MapPin className="h-3 w-3" /> Site</p>
                  <p className="font-medium">{docket.site.name || docket.site.address}</p>
                  {docket.site.name && docket.site.address && <p className="text-muted-foreground text-sm">{docket.site.address}</p>}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Line items (price-free) */}
        {docket?.items?.length > 0 && (
          <Card>
            <CardHeader><CardTitle>Work Performed</CardTitle></CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead>Unit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {docket.items.map((item, i) => (
                    <TableRow key={`item-${i}`}>
                      <TableCell className="font-medium">{item.description}</TableCell>
                      <TableCell className="text-right font-mono">{item.quantity ?? ''}</TableCell>
                      <TableCell className="capitalize">{item.uom}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Signature capture */}
        <Card className="border-[#ffd108]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileCheck className="h-5 w-5 text-[#ffd108]" />
              Confirm &amp; Sign
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="signedName">Your Name <span className="text-red-500">*</span></Label>
                <Input
                  id="signedName"
                  value={signedName}
                  onChange={(e) => setSignedName(e.target.value)}
                  placeholder="Full name"
                  data-testid="docket-sign-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="signedEmail">Your Email <span className="text-red-500">*</span></Label>
                <Input
                  id="signedEmail"
                  type="email"
                  value={signedEmail}
                  onChange={(e) => setSignedEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="docket-sign-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-1"><PenLine className="h-4 w-4" /> Signature <span className="text-red-500">*</span></Label>
                {hasSignature && (
                  <Button variant="ghost" size="sm" onClick={clearSignature} className="text-red-500 h-7 px-2">
                    <Trash2 className="h-3 w-3 mr-1" /> Clear
                  </Button>
                )}
              </div>
              <div className="border rounded-lg overflow-hidden bg-white">
                <canvas
                  ref={canvasRef}
                  width={600}
                  height={200}
                  className="w-full touch-none"
                  style={{ background: '#ffffff' }}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                  data-testid="docket-sign-canvas"
                />
              </div>
              <p className="text-muted-foreground text-xs text-center">Draw your signature above</p>
            </div>

            <p className="text-xs text-muted-foreground">
              By signing, you confirm the work described above was performed to your satisfaction.
            </p>

            <Button
              onClick={handleSubmit}
              disabled={processing || !signedName || !signedEmail || !hasSignature}
              className="w-full bg-green-600 hover:bg-green-700"
              data-testid="docket-sign-submit"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              {processing ? 'Submitting…' : 'Sign Docket'}
            </Button>
          </CardContent>
        </Card>

        <div className="text-center text-muted-foreground text-sm py-4">
          <p>Powered by EQUIPR CORE</p>
        </div>
      </div>
    </div>
  );
};

export default DocketSign;
